/**
 * Renders a Contentful `sectionLayout2` section of a CMS page: a centred heading, plain-text
 * paragraphs and an optional button over a full-width background image.
 *
 * Rendered by `ContentfulPage.tsx` for each section whose `content_type` is `sectionLayout2`. The
 * fields it reads must stay in `ContentfulSectionLayout2Fields`, which `ContentfulUtils.ts` →
 * `getPageContent` passes to `Crawl`.
 */
import React from "react";
import { useTheme } from "@ryerson/frontend.theme";
import { useApplication } from "@ryerson/frontend.application";
import { IContentfulBaseFields } from "./ContentfulPage";
import { Column, ContentSection, Grid, Row, SplitConfig } from "@ryerson/frontend.layout";
import { Typography } from "@ryerson/frontend.typography";
import styled from "@emotion/styled";
import { Button } from "@ryerson/frontend.button";
import { navigate } from "@ryerson/frontend.next";
import ContentfulUtils from "./ContentfulUtils";

/** A `sectionLayout2` entry: text and a call-to-action over a background image. */
export interface IContentfulSectionLayout2 extends IContentfulBaseFields {
	// The entry's name in Contentful; not rendered.
	name: string;
	// The text colour scheme for `Typography`; lower-cased by `ContentfulUtils.getType`.
	type: "primary" | "secondary" | "tertiary";
	// A Contentful asset, required. `file.url` is protocol-relative, so the component prefixes
	// `https:`. Its `title` and `description` are fetched but not rendered.
	background: {
		title: string;
		description: string;
		file: {
			url: string;
		};
	};
	title?: string;
	// Plain text; each line becomes a paragraph.
	description?: string;
	// The button shows only when both `buttonText` and `buttonLink` are set.
	buttonText?: string;
	// A path starting with `/` navigates in this app; anything else opens in a new tab.
	buttonLink?: string;
}

/**
 * The fields `getPageContent` keeps for this section, as dot paths under the page's `sections`
 * field.
 */
export const ContentfulSectionLayout2Fields = [
	"name",
	"type",
	"background.title",
	"background.description",
	"background.file.url",
	"title",
	"description",
	"buttonText",
	"buttonLink",
].map((field) => `sections.${field}`);

/** Props for `ContentfulSectionLayout2`: one `sectionLayout2` entry from a page's `sections`. */
export interface ContentfulSectionLayout2Props {
	content: IContentfulSectionLayout2;
}

const ContentCenter = styled.div`
	text-align: center;
`;

const AdjustBackground = styled.div`
	img {
		object-fit: cover;
		height: 100%;
	}
`;

/** Renders one `sectionLayout2` section; see the file header. */
const ContentfulSectionLayout2: React.FC<ContentfulSectionLayout2Props> = ({ content }) => {
	const { theme } = useTheme();
	const {
		localization: { language },
	} = useApplication();

	const splitConfig: SplitConfig = {
		splitImage: [
			{
				imageUrl: "https:" + content.background.file.url,
				percentage: "100%",
			},
		],
	};

	const type = ContentfulUtils.getType(content.type);

	return (
		<AdjustBackground>
			<ContentSection type="split" split={splitConfig} vPadding="60px">
				<Grid gutter={20}>
					<Row>
						<Column lg={8} offset-lg={2} sm={10} offset-sm={1}>
							<ContentCenter>
								{content.title && (
									<Typography type={type} variant="h1">
										{content.title}
									</Typography>
								)}
								{content.description &&
									content.description.split("\n").map((paragraph, index) => (
										<Typography key={index} type={type} variant="p">
											{paragraph}
										</Typography>
									))}
								{content.buttonLink && content.buttonText && (
									<Button
										label={content.buttonText}
										type="primary"
										onClick={() => {
											if (content.buttonLink) {
												if (content.buttonLink.startsWith("/")) {
													// In production a `/store...` link is
													// turned into a full reload by
													// `pages/_app.tsx` → `App`.
													navigate(content.buttonLink);
													return;
												} else {
													window.open(content.buttonLink, "_blank");
													return;
												}
											}
										}}
										css={{ display: "inline-block" }}
									/>
								)}
							</ContentCenter>
						</Column>
					</Row>
				</Grid>
			</ContentSection>
		</AdjustBackground>
	);
};

export default ContentfulSectionLayout2;
